import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { PreviewQuestionAnswerDetailsSaved } from '../buyer-preview.models';
import { FormResponseService } from './form-response.service';
import { MultiSectionFormPreviewService } from './multi-section-form-preview.service';
import { ValidateResponseService } from './validate-response.service';
import { BuyerPreviewService } from '../buyer-preview.service';
import { HandleConditionalLogicService } from './handle-conditional-logic.service';
import { HandleAnswerService } from '../handle-answer.service';
import { validateAttachment } from '../../shared/attachment-validation';
import Swal from 'sweetalert2';

@Directive({
    selector: '[recognizeInput]',
    standalone: true,
})
export class RecognizeInputDirective implements OnDestroy, AfterViewInit, OnInit {
  sanitizer = inject(DomSanitizer);
  msfps = inject(MultiSectionFormPreviewService);
  fr = inject(FormResponseService);
  vrs = inject(ValidateResponseService);
  buyerPreviewService = inject(BuyerPreviewService);
  hcl = inject(HandleConditionalLogicService);
  has = inject(HandleAnswerService);
  private elementRef = inject(ElementRef);
  private mutationObserver: MutationObserver | null = null;
  private initialEffectsApplied = false;

  ngOnInit() {
    // Set up MutationObserver to detect when new elements are added to the DOM
    this.setupMutationObserver();
  }

  /**
   * After the view is initialized, apply any stored conditional effects
   */
  ngAfterViewInit() {
    this.applyStoredConditionalEffects();
    // Also scan for saved answers and apply their conditional effects
    this.applySavedAnswerConditionalEffects();
  }

  /**
   * Scans for radio buttons with data attributes and applies their conditional effects
   * based on saved answers. This handles the case when form is reloaded with saved data.
   */
  applySavedAnswerConditionalEffects() {
    // Only run once on initial load
    if (this.initialEffectsApplied) {
      return;
    }
    this.initialEffectsApplied = true;
    
    // Delay to ensure DOM is fully rendered
    setTimeout(() => {
      this.applyEffectsFromSavedAnswers();
    }, 500);
    
    // Also run again after a longer delay in case fields load slowly
    setTimeout(() => {
      this.applyEffectsFromSavedAnswers();
    }, 1500);
  }

  /**
   * Applies conditional effects based on saved answers from the data model.
   * Uses msfps.allFields to get the saved answers and applies effects to target fields.
   * Works even when the source question (Q2.0) is not in the current DOM view.
   */
  applyEffectsFromSavedAnswers() {
    // Check if allFields is available
    if (!this.msfps.allFields || this.msfps.allFields.length === 0) {
      return;
    }
    
    // Find all radio fields that have saved answers and conditional logic
    this.msfps.allFields.forEach((field: any) => {
      if (!field.data || !field.data[1]) return;
      
      const fieldType = field.data[1].type;
      const questionId = field.data[0]?.QuestionId;
      
      // Only process radio fields that have answers
      if (fieldType === 'radio' && field.answered && field.nodeLogic?.selectedAnswers?.length > 0) {
        const selectedAnswer = field.nodeLogic.selectedAnswers[0];
        const selectedAnswerText = typeof selectedAnswer === 'object' ? selectedAnswer.answer : selectedAnswer;
        
        // Find the radio button in radiodata that matches the selected answer
        const radioData = field.data[1].radiodata;
        if (!radioData) return;
        
        const selectedRadioOption = radioData.find((opt: any) => 
          opt.answer === selectedAnswerText || opt.selected === true
        );
        
        if (!selectedRadioOption) return;
        
        // Get the HTML content to find data attributes
        const questionHtml = field.data[1]?.question?.changingThisBreaksApplicationSecurity;
        if (!questionHtml) return;
        
        // Parse the HTML to find radio buttons with data attributes
        const parser = new DOMParser();
        const doc = parser.parseFromString(questionHtml, 'text/html');
        const radioButtons = doc.querySelectorAll('input[type="radio"]');
        
        radioButtons.forEach((radio: HTMLInputElement) => {
          // Check if this radio's value matches the selected option
          if (radio.value === selectedRadioOption.optionid) {
            // Check for data-set-target attribute
            const setTarget = radio.getAttribute('data-set-target');
            const setValue = radio.getAttribute('data-set-value');
            const setDisabled = radio.getAttribute('data-set-disabled');
            
            if (setTarget) {
              // Apply the effect to the target element if it exists in DOM
              const targetEl = document.getElementById(setTarget) as HTMLSelectElement | HTMLInputElement;
              if (targetEl && !targetEl.disabled) {
                this.storeAndApplyEffect(setTarget, setValue, setDisabled);
              } else if (!targetEl) {
                // Target not in DOM yet, store the effect for later
                this.buyerPreviewService.conditionalEffects.set(setTarget, {
                  value: setValue,
                  disabled: setDisabled === 'true'
                });
              }
            }
            
            // Handle numbered targets
            let index = 1;
            let numberedTarget = radio.getAttribute(`data-set-target${index}`);
            while (numberedTarget) {
              const numberedValue = radio.getAttribute(`data-set-value${index}`);
              const numberedDisabled = radio.getAttribute(`data-set-disabled${index}`);
              
              const numberedTargetEl = document.getElementById(numberedTarget) as HTMLSelectElement | HTMLInputElement;
              if (numberedTargetEl && !numberedTargetEl.disabled) {
                this.storeAndApplyEffect(numberedTarget, numberedValue, numberedDisabled);
              } else if (!numberedTargetEl) {
                this.buyerPreviewService.conditionalEffects.set(numberedTarget, {
                  value: numberedValue,
                  disabled: numberedDisabled === 'true'
                });
              }
              
              index++;
              numberedTarget = radio.getAttribute(`data-set-target${index}`);
            }
          }
        });
      }
    });
  }

  /**
   * Applies only the SET effects (set value + disable) without clearing other targets.
   * Used on page load to restore disabled state without clearing saved answers.
   */
  applySetEffectsOnly(element: HTMLInputElement) {
    const setTarget = element.dataset['setTarget'];
    const setValue = element.dataset['setValue'];
    const setDisabled = element.dataset['setDisabled'];

    if (setTarget) {
      this.storeAndApplyEffect(setTarget, setValue, setDisabled);
    }

    // Handle numbered targets
    let index = 1;
    while (element.dataset[`setTarget${index}`]) {
      const target = element.dataset[`setTarget${index}`];
      const value = element.dataset[`setValue${index}`];
      const disabled = element.dataset[`setDisabled${index}`];
      this.storeAndApplyEffect(target, value, disabled);
      index++;
    }
    // Note: Intentionally NOT calling clearEffectFromTarget here
  }

  /**
   * Sets up a MutationObserver to detect DOM changes and apply conditional effects
   */
  setupMutationObserver() {
    if (this.mutationObserver) {
      return;
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      // Check if any select or input elements were added
      const hasRelevantChanges = mutations.some(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          return Array.from(mutation.addedNodes).some(node => {
            if (node instanceof HTMLElement) {
              return node.querySelector('select, input') !== null || 
                     node.tagName === 'SELECT' || 
                     node.tagName === 'INPUT';
            }
            return false;
          });
        }
        return false;
      });

      if (hasRelevantChanges) {
        this.applyStoredConditionalEffects();
        // Also apply saved answer effects when new elements are added (e.g., navigating to a section)
        this.applyEffectsFromSavedAnswers();
      }
    });

    // Observe the entire document body for changes
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Applies any stored conditional effects to elements within this directive's host element
   */
  applyStoredConditionalEffects() {
    if (this.buyerPreviewService.conditionalEffects.size === 0) {
      return;
    }

    // Small delay to ensure DOM is fully rendered
    setTimeout(() => {
      this.buyerPreviewService.conditionalEffects.forEach((effect, targetId) => {
        const targetEl = document.getElementById(targetId) as HTMLSelectElement | HTMLInputElement;
        if (targetEl) {
          const questionId = targetId.replace('Dropdown_', '').replace('Radio_', '').replace('Checkbox_', '');
          
          // Handle "clear" effect - reset the field
          if (effect.clear) {
            targetEl.disabled = false;
            targetEl.style.backgroundColor = '';
            if (targetEl.tagName === 'SELECT') {
              (targetEl as HTMLSelectElement).selectedIndex = 0;
            } else {
              targetEl.value = '';
            }
            // Clear field data and remove from stored effects
            this.clearFieldData(questionId);
            this.buyerPreviewService.conditionalEffects.delete(targetId);
            return;
          }
          
          // Handle "set" effect - set value and disable
          if (effect.value !== undefined && effect.value !== null) {
            targetEl.value = effect.value;
            
            // Update localStorage with proper text value
            const selectedOption = targetEl.tagName === 'SELECT' 
              ? (targetEl as HTMLSelectElement).options[(targetEl as HTMLSelectElement).selectedIndex]?.text 
              : effect.value;
            
            const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
              modifiedField: null,
              answerUniqueId: [effect.value],
              answerValue: [selectedOption || effect.value],
              isAnswerValid: true,
              isAnswerModified: true,
            };
            localStorage.setItem(questionId, JSON.stringify(fieldAnswer));

            // Also update the field's nodeLogic.selectedAnswers in the data model
            this.updateFieldNodeLogic(questionId, effect.value);
          }
          if (effect.disabled) {
            targetEl.disabled = true;
            targetEl.style.backgroundColor = '#e0e0e0';
          }
        }
      });
    }, 150);
  }

  /**
   * Updates the field's nodeLogic.selectedAnswers in the data model
   * @param questionId - The question ID
   * @param answerValueId - The GUID/ID of the selected answer
   */
  updateFieldNodeLogic(questionId: string, answerValueId: string) {
    const field = this.msfps.allFields?.find(
      (f) => f.data[0]?.QuestionId === questionId
    );
    
    if (field) {
      // Get the display text from the field's dropdown data
      let answerDisplayText = answerValueId;
      let selectedOptionData: any = null;
      
      if (field.data[1]?.dropdowndata) {
        const selectedOption = field.data[1].dropdowndata.find(
          (opt: any) => opt.optionid === answerValueId
        );
        if (selectedOption) {
          answerDisplayText = selectedOption.answer;
          selectedOptionData = { ...selectedOption, selected: true };
          // Also mark this option as selected in the dropdown data
          field.data[1].dropdowndata.forEach((opt: any) => {
            opt.selected = opt.optionid === answerValueId;
          });
        }
      }

      // For dropdown type, selectedAnswers should be IOptioningData[] format
      // The getSelectedAnswers method in service expects this format for dropdowns
      if (field.nodeLogic) {
        if (selectedOptionData) {
          // For dropdown/radio/checkbox, use the full option object
          field.nodeLogic.selectedAnswers = [selectedOptionData];
        } else {
          field.nodeLogic.selectedAnswers = [answerDisplayText];
        }
      } else {
        field.nodeLogic = { 
          selectedAnswers: selectedOptionData ? [selectedOptionData] : [answerDisplayText] 
        } as any;
      }
      field.answered = true;
      field.valid = true;
      field.isAnswerModified = true;

      // Update localStorage with correct display text
      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: null,
        answerUniqueId: [answerValueId],
        answerValue: [answerDisplayText],
        isAnswerValid: true,
        isAnswerModified: true,
      };
      localStorage.setItem(questionId, JSON.stringify(fieldAnswer));

      // Trigger conditional logic for dependent questions (hide/show)
      // This ensures that if Q3.0 is auto-set to "No", Q3.1/Q3.2 will be hidden
      this.hcl.toggleConditionalNodesFor(field, 'clickAction');
    }
  }

  private _formLayout = '';
  public get formLayout() {
    let localStorageFormLayout = localStorage.getItem('FormLayout');
    return (this._formLayout = localStorageFormLayout ?? '');
  }

  private _orgFormId = '';
  public get orgFormId() {
    let localStorageOrgFormId = localStorage.getItem('OrgFormId');
    return (this._orgFormId = localStorageOrgFormId ?? '');
  }

  constructor() {}

  private saveTimeoutId: any;

  // SM-1679: Track initialized elements to prevent duplicate event listeners
  // Using WeakSet to avoid memory leaks when elements are removed from DOM
  private initializedElements = new WeakSet<HTMLElement>();

  private debouncedSave(e: Event, delayMs: number = 300): void {
    if (this.saveTimeoutId) {
      clearTimeout(this.saveTimeoutId);
    }
    this.saveTimeoutId = setTimeout(() => {
      this.locallySaveInputElementValue(e);
    }, delayMs);
  }

  ngOnDestroy(): void {
    if (this.saveTimeoutId) {
      clearTimeout(this.saveTimeoutId);
      this.saveTimeoutId = null;
    }
    // Clean up MutationObserver
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    // WeakSet automatically releases references when elements are garbage collected
  }

  @HostListener('click', ['$event'])
  @HostListener('input', ['$event'])
  @HostListener('blur', ['$event'])
  @HostListener('keydown', ['$event'])
  setHelper(e: Event) {
    const eventType = e.type;

    // SM-1679: Handle Tab key navigation for validation icon sync
    if (
      eventType === 'click' ||
      eventType === 'blur' ||
      (eventType === 'keydown' && (e as KeyboardEvent).key === 'Tab')
    ) {
      // Persist immediately on click, blur, and Tab key navigation.
      this.locallySaveInputElementValue(e);
    } else if (eventType === 'input') {
      // Debounce saves while the user is typing to avoid excessive I/O.
      this.debouncedSave(e);
    }
  }
  swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: 'btn',
    },
    buttonsStyling: false,
  });

  locallySaveInputElementValue(e: Event) {
    if ((e.target as HTMLInputElement).tagName === 'SELECT') {
      this.handleSingleSelectDropdown(e);
    } else if ((e.target as HTMLInputElement).tagName === 'INPUT') {
      const eventTarget = e.target as HTMLInputElement;
      if (eventTarget.type === 'date') {
        this.handleDateInput(eventTarget);
      } else if (eventTarget.type === 'number') {
        this.handleNumberInput(eventTarget);
      } else if (['text', 'email', 'tel'].includes(eventTarget.type)) {
        this.handleTextInput(eventTarget);
      } else if (eventTarget.type === 'checkbox') {
        this.handleCheckBoxInput(e);
      } else if (eventTarget.type === 'radio') {
        this.handleRadioInput(e);
      } else if (eventTarget.type === 'file') {
        this.handleFileInput(eventTarget);
      }
    } else if (
      (e.target as HTMLOptionElement).tagName === 'OPTION' &&
      ((e.target as HTMLOptionElement).parentElement as HTMLSelectElement)
        .multiple
    ) {
      this.handleMultiSelectDropdown(e);
    } else if ((e.target as HTMLTextAreaElement).tagName === 'TEXTAREA') {
      this.handleTextAreaInput(e);
    }
  }

  handleRadioInput(e: Event) {
    const eventTarget = e.target as HTMLInputElement;
    const selectedRadioEle = eventTarget;

    const isChecked = eventTarget.checked;
    const selection = isChecked ? (eventTarget.nextSibling as Text).data : null;

    if (selection && isChecked) {
      selectedRadioEle.setAttribute('checked', 'true');
      selectedRadioEle.checked = true;
    } else {
      selectedRadioEle.removeAttribute('selected');
      selectedRadioEle.checked = false;
    }
    //apply checked attribute to the selected Radio

    const modifiedFieldEle = document.createElement('div');
    modifiedFieldEle.setAttribute('style', 'display:flex;justify-content:left');
    modifiedFieldEle.innerHTML = eventTarget.outerHTML;

    const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(eventTarget.name.replace('Radio_', '').toString())
    );

    const latestFieldAnswerValue = selection ? [selection] : null;

    const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
      modifiedField: this.sanitizer.bypassSecurityTrustHtml(
        modifiedFieldEle.outerHTML
      ),
      answerUniqueId: [eventTarget.value],
      answerValue: latestFieldAnswerValue,
      isAnswerValid: Boolean(eventTarget.value),
      isAnswerModified: !(
        oldFieldAnswer?.answerValue?.toString() ===
        latestFieldAnswerValue?.toString()
      ),
    };
    localStorage.setItem(
      eventTarget.name.replace('Radio_', '').toString(),
      JSON.stringify(fieldAnswer)
    );

    // Handle generic conditional logic based on data attributes - only when actually checked
    if (isChecked && selection) {
      this.handleConditionalLogic(eventTarget);
    }

    const activeFieldId = eventTarget.name.replace('Radio_', '').toString();
    this.msfps.helper$.next({
      activeQuestionId: activeFieldId,
      val: 'clicked on preview question input element',
    });
  }

  /**
   * Generic conditional logic handler using data attributes.
   * 
   * Supported data attributes on radio/checkbox inputs:
   * - data-set-target: Target element ID (dropdown/input) to modify
   * - data-set-value: Value to set on the target element
   * - data-set-disabled: "true" to disable the target, "false" to enable
   * - data-clear-target: Target element ID to clear/enable when this option is selected
   */
  handleConditionalLogic(eventTarget: HTMLInputElement) {
    // Get all radio buttons in the same group to clear their effects first
    const radioGroup = document.querySelectorAll(`input[name="${eventTarget.name}"]`);
    
    // SM-3381: Capture snapshot of currently-locked targets (disabled: true) BEFORE
    // clearConditionalEffects() mutates the map. This allows clearEffectFromTarget()
    // to distinguish between locked targets (auto-set by conditional logic) and
    // free-choice user answers that should be preserved.
    const previouslyLockedTargets = new Set<string>();
    this.buyerPreviewService.conditionalEffects.forEach((effect, targetId) => {
      if (effect.disabled === true) {
        previouslyLockedTargets.add(targetId);
      }
    });
    
    // First, clear/enable any targets that other radio options may have disabled
    radioGroup.forEach((radio: HTMLInputElement) => {
      if (radio !== eventTarget) {
        this.clearConditionalEffects(radio);
      }
    });

    // Apply conditional logic for the selected option, passing the snapshot
    this.applyConditionalEffects(eventTarget, previouslyLockedTargets);
  }

  /**
   * Clears the conditional effects of a radio option
   */
  clearConditionalEffects(element: HTMLInputElement) {
    const setTarget = element.dataset['setTarget'];
    if (setTarget) {
      this.buyerPreviewService.conditionalEffects.delete(setTarget);
      const targetEl = document.getElementById(setTarget) as HTMLSelectElement | HTMLInputElement;
      if (targetEl) {
        targetEl.disabled = false;
        targetEl.style.backgroundColor = '';
      }
    }

    // Handle numbered targets
    let index = 1;
    while (element.dataset[`setTarget${index}`]) {
      const target = element.dataset[`setTarget${index}`];
      this.buyerPreviewService.conditionalEffects.delete(target);
      const targetEl = document.getElementById(target) as HTMLSelectElement | HTMLInputElement;
      if (targetEl) {
        targetEl.disabled = false;
        targetEl.style.backgroundColor = '';
      }
      index++;
    }
  }

  /**
   * Applies conditional effects based on data attributes
   * @param element - The input element with data attributes
   * @param previouslyLockedTargets - Optional set of target IDs that were locked (auto-set)
   *   before this operation. Used by clearEffectFromTarget to preserve user's free-choice answers.
   */
  applyConditionalEffects(element: HTMLInputElement, previouslyLockedTargets?: Set<string>) {
    const setTarget = element.dataset['setTarget'];
    const setValue = element.dataset['setValue'];
    const setDisabled = element.dataset['setDisabled'];
    const clearTarget = element.dataset['clearTarget'];

    if (setTarget) {
      this.storeAndApplyEffect(setTarget, setValue, setDisabled);
    }

    if (clearTarget) {
      this.clearEffectFromTarget(clearTarget, previouslyLockedTargets);
    }

    // Handle numbered targets
    let index = 1;
    while (element.dataset[`setTarget${index}`]) {
      const target = element.dataset[`setTarget${index}`];
      const value = element.dataset[`setValue${index}`];
      const disabled = element.dataset[`setDisabled${index}`];
      this.storeAndApplyEffect(target, value, disabled);
      index++;
    }

    // Handle numbered clear targets
    index = 1;
    while (element.dataset[`clearTarget${index}`]) {
      const target = element.dataset[`clearTarget${index}`];
      this.clearEffectFromTarget(target, previouslyLockedTargets);
      index++;
    }
  }

  /**
   * Stores effect in service and applies to target if it exists in DOM
   */
  storeAndApplyEffect(targetId: string, value?: string, disabled?: string) {
    const questionId = targetId.replace('Dropdown_', '').replace('Radio_', '').replace('Checkbox_', '');
    
    // Store in service for when the question is rendered
    this.buyerPreviewService.conditionalEffects.set(targetId, {
      value: value,
      disabled: disabled === 'true'
    });

    // Try to apply immediately if element exists in DOM
    const targetEl = document.getElementById(targetId) as HTMLSelectElement | HTMLInputElement;
    let answerText = value;
    
    if (targetEl && value !== undefined && value !== null) {
      targetEl.value = value;
      // Get the display text for the selected option
      if (targetEl.tagName === 'SELECT') {
        answerText = (targetEl as HTMLSelectElement).options[(targetEl as HTMLSelectElement).selectedIndex]?.text || value;
      }
      if (disabled === 'true') {
        targetEl.disabled = true;
        targetEl.style.backgroundColor = '#e0e0e0';
      }
    }

    // Update localStorage
    if (value !== undefined && value !== null) {
      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: null,
        answerUniqueId: [value],
        answerValue: [answerText],
        isAnswerValid: true,
        isAnswerModified: true,
      };
      localStorage.setItem(questionId, JSON.stringify(fieldAnswer));
    }

    // Update the field's nodeLogic.selectedAnswers in the data model
    if (value !== undefined && value !== null) {
      this.updateFieldNodeLogic(questionId, answerText);
    }

    this.msfps.helper$.next({
      activeQuestionId: '',
      val: 'conditional logic applied',
    });
  }

  /**
   * Clears effect from a target element.
   * SM-3381: If previouslyLockedTargets is provided, only clears targets that were
   * auto-set/locked by conditional logic. User's free-choice answers are preserved.
   * @param targetId - The DOM ID of the target element to clear
   * @param previouslyLockedTargets - Optional set of target IDs that were locked before this operation
   */
  clearEffectFromTarget(targetId: string, previouslyLockedTargets?: Set<string>) {
    const questionId = targetId.replace('Dropdown_', '').replace('Radio_', '').replace('Checkbox_', '');
    
    // SM-3381: If previouslyLockedTargets is provided and this target was NOT locked,
    // it means the user made a free-choice selection. Preserve their answer.
    if (previouslyLockedTargets && !previouslyLockedTargets.has(targetId)) {
      return;
    }
    
    // Remove from effects Map first
    this.buyerPreviewService.conditionalEffects.delete(targetId);
    
    // Clear localStorage for this question
    localStorage.removeItem(questionId);
    
    // Clear the field's data model first (this is the source of truth for Angular)
    const fieldIndex = this.msfps.allFields?.findIndex(
      (f) => f.data[0]?.QuestionId === questionId
    );
    
    if (fieldIndex !== -1 && fieldIndex !== undefined) {
      const field = this.msfps.allFields[fieldIndex];
      
      // Reset the field state
      if (field.nodeLogic) {
        field.nodeLogic.selectedAnswers = [];
      }
      field.answered = false;
      field.valid = false;
      field.isAnswerModified = true;
      
      // Reset dropdown selection state
      if (field.data[1]?.dropdowndata) {
        field.data[1].dropdowndata.forEach((opt: any) => {
          opt.selected = false;
        });
      }
      
      // Reset the dropdown HTML to clear the selected attribute
      if (field.data[1]?.type === 'dropdown' && field.data[1]?.question?.changingThisBreaksApplicationSecurity) {
        const parser = new DOMParser();
        const html = parser.parseFromString(
          field.data[1].question.changingThisBreaksApplicationSecurity,
          'text/html'
        );
        Array.from(html.getElementsByTagName('select')).forEach((selectEl) => {
          Array.from(selectEl.options).forEach((option: HTMLOptionElement) => {
            option.removeAttribute('selected');
            option.selected = false;
          });
        });
        field.data[1].question.changingThisBreaksApplicationSecurity = html.body.innerHTML;
      }
      
      // Force update the field in the array
      this.msfps.allFields[fieldIndex] = { ...field };
      
      // Also update in fieldsSegregatedIntoSections if it exists
      if (this.msfps.fieldsSegregatedIntoSections && field.sectionId) {
        const section = this.msfps.fieldsSegregatedIntoSections[field.sectionId];
        if (section && section.fieldsList) {
          const sectionFieldIndex = section.fieldsList.findIndex(
            (f: any) => f.data[0]?.QuestionId === questionId
          );
          if (sectionFieldIndex !== -1) {
            section.fieldsList[sectionFieldIndex] = this.msfps.allFields[fieldIndex];
          }
        }
      }
      
      // Now trigger conditional logic for dependent questions
      setTimeout(() => {
        this.hcl.toggleConditionalNodesFor(this.msfps.allFields[fieldIndex], 'clickAction');
      }, 150);
    }
    
    // Reset DOM element AFTER data model update
    const targetEl = document.getElementById(targetId) as HTMLSelectElement | HTMLInputElement;
    if (targetEl) {
      targetEl.disabled = false;
      targetEl.style.backgroundColor = '';
      if (targetEl.tagName === 'SELECT') {
        const selectEl = targetEl as HTMLSelectElement;
        // Find the first option (usually "Select option" or empty)
        if (selectEl.options.length > 0) {
          selectEl.selectedIndex = 0;
          selectEl.value = selectEl.options[0].value;
        }
      } else {
        targetEl.value = '';
      }
    }
    
    // Notify the UI to refresh
    this.msfps.helper$.next({
      activeQuestionId: questionId,
      val: 'conditional logic cleared',
    });
  }

  /**
   * Clears field data from the data model
   */
  clearFieldData(questionId: string) {
    const fieldIndex = this.msfps.allFields?.findIndex(
      (f) => f.data[0]?.QuestionId === questionId
    );
    
    if (fieldIndex !== -1 && fieldIndex !== undefined) {
      const field = this.msfps.allFields[fieldIndex];
      
      // Reset the field state
      if (field.nodeLogic) {
        field.nodeLogic.selectedAnswers = [];
      }
      field.answered = false;
      field.valid = false;
      field.isAnswerModified = true;
      
      // Reset dropdown selection state
      if (field.data[1]?.dropdowndata) {
        field.data[1].dropdowndata.forEach((opt: any) => {
          opt.selected = false;
        });
      }
      
      // Force update the field in the array to trigger change detection
      this.msfps.allFields[fieldIndex] = { ...field };
      
      // Also update in fieldsSegregatedIntoSections if it exists
      if (this.msfps.fieldsSegregatedIntoSections && field.sectionId) {
        const section = this.msfps.fieldsSegregatedIntoSections[field.sectionId];
        if (section && section.fieldsList) {
          const sectionFieldIndex = section.fieldsList.findIndex(
            (f: any) => f.data[0]?.QuestionId === questionId
          );
          if (sectionFieldIndex !== -1) {
            section.fieldsList[sectionFieldIndex] = this.msfps.allFields[fieldIndex];
          }
        }
      }
      
      // Clear localStorage for this question
      localStorage.removeItem(questionId);

      // Trigger conditional logic for dependent questions (hide/show)
      // This ensures dependent questions are re-evaluated after clearing
      this.hcl.toggleConditionalNodesFor(this.msfps.allFields[fieldIndex], 'clickAction');
    }
  }

  handleCheckBoxInput(e: Event) {
    const dynamicCheckboxLabelPrefix = /Checkbox_Label(\d+)_/g;
    const eventTarget = e.target as HTMLInputElement;

    const checkboxWrapperTarget =
      eventTarget?.parentElement?.parentElement?.parentElement;

    checkboxWrapperTarget.setAttribute(
      'id',
      eventTarget.parentElement.id
        ?.replace(dynamicCheckboxLabelPrefix, 'Checkbox_WRAPPER_')
        ?.toString()
    );

    const allCheckboxContainersArr = Array.from(checkboxWrapperTarget.children);
    const allCheckboxesArr = [...allCheckboxContainersArr]?.map(
      (x) => x?.firstChild?.firstChild as HTMLInputElement
    );

    if (allCheckboxesArr?.length !== 0) {
      const checkedOptionsArr = allCheckboxesArr.filter((cb) => cb.checked);

      // Remove checked attribute and set checked=false for all options
      allCheckboxesArr.forEach((option: HTMLInputElement) => {
        option.removeAttribute('checked');
        option.checked = false;
      });

      // Add checked attribute and set checked=true for checked options
      checkedOptionsArr.forEach((checkedOptionEle: HTMLInputElement) => {
        checkedOptionEle.setAttribute('checked', 'true');
        checkedOptionEle.checked = true;
      });

      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left'
      );
      modifiedFieldEle.innerHTML = checkboxWrapperTarget.outerHTML;

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(
          checkboxWrapperTarget.id.replace('Checkbox_WRAPPER_', '').toString()
        )
      );

      // If nothing is checked, set answerValue to null
      const latestFieldAnswerValue = checkedOptionsArr.length
        ? checkedOptionsArr.map((item) => (item.nextSibling as Text).data)
        : null;

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML
        ),
        answerUniqueId: checkedOptionsArr.map((item) => item.id),
        answerValue: latestFieldAnswerValue,
        isAnswerValid: Boolean(checkedOptionsArr?.length),
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          (latestFieldAnswerValue ? latestFieldAnswerValue.toString() : '')
        ),
      };
      localStorage.setItem(
        checkboxWrapperTarget.id.replace('Checkbox_WRAPPER_', '').toString(),
        JSON.stringify(fieldAnswer)
      );

      const activeFieldId = checkboxWrapperTarget.id
        .replace('Checkbox_WRAPPER_', '')
        .toString();

      this.msfps.helper$.next({
        activeQuestionId: activeFieldId,
        val: 'clicked on preview question input element',
      });
    }
  }

  handleSingleSelectDropdown(e: Event) {
    const eventTarget = e.target as HTMLSelectElement;
    const optionsArr = Array.from(eventTarget.options as HTMLOptionsCollection);
    const selectedOptionEle = optionsArr.find(
      (option: HTMLOptionElement) => option.value === eventTarget.value
    );

    const selection = selectedOptionEle.text;

    //apply selected attribute to the selected option
    optionsArr.forEach((option: HTMLOptionElement) => {
      option.removeAttribute('selected');
      option.selected = false;
    });

    if (selection.toLowerCase() !== 'select option') {
      selectedOptionEle.setAttribute('selected', 'true');
      selectedOptionEle.selected = true;
    }
    //apply selected attribute to the selected option

    const modifiedFieldEle = document.createElement('div');
    modifiedFieldEle.setAttribute('style', 'display:flex;justify-content:left');
    modifiedFieldEle.innerHTML = eventTarget.outerHTML;

    const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(eventTarget.id.replace('Dropdown_', '').toString())
    );

    const latestFieldAnswerValue =
      selection.toLowerCase() !== 'select option' ? [selection] : null;

    const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
      modifiedField: this.sanitizer.bypassSecurityTrustHtml(
        modifiedFieldEle.outerHTML
      ),
      answerUniqueId: [eventTarget.value],
      answerValue: latestFieldAnswerValue,
      isAnswerValid: Boolean(eventTarget.value),
      isAnswerModified: !(
        oldFieldAnswer?.answerValue?.toString() ===
        latestFieldAnswerValue?.toString()
      ),
    };

    localStorage.setItem(
      eventTarget.id.replace('Dropdown_', '').toString(),
      JSON.stringify(fieldAnswer)
    );

    const activeFieldId = eventTarget.id.replace('Dropdown_', '').toString();
    this.msfps.helper$.next({
      activeQuestionId: activeFieldId,
      val: 'clicked on preview question input element',
    });
  }

  handleMultiSelectDropdown(e: Event) {
    const multiSelectTarget = (e.target as HTMLOptionElement)
      .parentElement as HTMLSelectElement;
    const allOptionsArr = Array.from(
      multiSelectTarget.options as HTMLOptionsCollection
    );
    if (multiSelectTarget.selectedOptions) {
      const selectedOptionsArr = Array.from(
        multiSelectTarget.selectedOptions as HTMLOptionsCollection
      );

      // remove select attribute for all the existing options
      allOptionsArr.forEach((option: HTMLOptionElement) => {
        option.removeAttribute('selected');
        option.selected = false;
      });

      //add select attribute for all the selected options
      selectedOptionsArr.forEach((selectedOptionEle: HTMLOptionElement) => {
        selectedOptionEle.setAttribute('selected', 'true');
        selectedOptionEle.selected = true;
      });

      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left'
      );
      modifiedFieldEle.innerHTML = multiSelectTarget.outerHTML;

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(
          multiSelectTarget.id.replace('MultiselectDropdown_', '').toString()
        )
      );

      const latestFieldAnswerValue = selectedOptionsArr.map(
        (item) => item.text
      );

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML
        ),
        answerUniqueId: selectedOptionsArr.map((item) => item.value),
        answerValue: latestFieldAnswerValue,
        isAnswerValid: Boolean(multiSelectTarget.value),
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(
        multiSelectTarget.id.replace('MultiselectDropdown_', '').toString(),
        JSON.stringify(fieldAnswer)
      );

      const activeFieldId = multiSelectTarget.id
        .replace('MultiselectDropdown_', '')
        .toString();

      this.msfps.helper$.next({
        activeQuestionId: activeFieldId,
        val: 'clicked on preview question input element',
      });
    }
  }

  handleDateInput(eventTarget: HTMLInputElement) {
    // SM-1679: Guard against duplicate event listeners
    if (this.initializedElements.has(eventTarget)) return;
    this.initializedElements.add(eventTarget);

    eventTarget.addEventListener('input', (dateInputEvent) => {

      eventTarget.removeAttribute('value');

      if (eventTarget.value)
        eventTarget.setAttribute('value', eventTarget.value);
      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left'
      );
      modifiedFieldEle.innerHTML = eventTarget.outerHTML;

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(eventTarget.id.toString())
      );

      const latestFieldAnswerValue = eventTarget.value
        ? [eventTarget.value]
        : null;

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML
        ),
        answerUniqueId: [eventTarget.id],
        answerValue: latestFieldAnswerValue,
        isAnswerValid: Boolean(eventTarget.value),
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(eventTarget.id, JSON.stringify(fieldAnswer));

      const activeFieldId = eventTarget.id;

      this.msfps.helper$.next({
        activeQuestionId: activeFieldId,
        val: 'clicked on preview question input element',
      });
    });
  }

  handleNumberInput(eventTarget: HTMLInputElement) {
    // SM-1679: Guard against duplicate event listeners
    if (this.initializedElements.has(eventTarget)) return;
    this.initializedElements.add(eventTarget);

    eventTarget.addEventListener('blur', () => {

      if (eventTarget.value)
        eventTarget.setAttribute('value', eventTarget.value);
      const modifiedFieldEle = document.createElement('div');
      if (eventTarget.validity.valid) {
        modifiedFieldEle.setAttribute(
          'style',
          'display:flex; justify-content:left'
        );
      } else {
        modifiedFieldEle.setAttribute('style', 'text-align:left');
      }
      modifiedFieldEle.innerHTML = eventTarget.outerHTML;

      // VALIDATION
      const inputValidationResult = this.vrs.validateInput(eventTarget);

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(
          eventTarget.id.replace('textboxID_', '').toString()
        )
      );

      const latestFieldAnswerValue = eventTarget.value
        ? [eventTarget.value]
        : null;

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML
        ),
        answerUniqueId: [eventTarget.id],
        answerValue: latestFieldAnswerValue,
        isAnswerValid: inputValidationResult.isInputValid,
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };
      localStorage.setItem(
        eventTarget.id.replace('textboxID_', ''),
        JSON.stringify(fieldAnswer)
      );

      const activeFieldId = eventTarget.id.replace('textboxID_', '');

      this.msfps.helper$.next({
        activeQuestionId: activeFieldId,
        val: 'clicked on preview question input element',
      });
    });
  }

  handleTextInput(eventTarget: HTMLInputElement) {
    // SM-1679: Guard against duplicate event listeners
    if (this.initializedElements.has(eventTarget)) return;
    this.initializedElements.add(eventTarget);

    eventTarget.addEventListener('blur', (textInputEvent) => {
      if (eventTarget.value)
        eventTarget.setAttribute('value', eventTarget.value);

      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left'
      );
      modifiedFieldEle.innerHTML = eventTarget.outerHTML;

      const inputValidationResult = this.vrs.validateInput(eventTarget);

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(
          eventTarget.id.replace('textboxID_', '').toString()
        )
      );

      const latestFieldAnswerValue = eventTarget.value
        ? [eventTarget.value]
        : null;

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML
        ),
        answerUniqueId: [eventTarget.id],
        answerValue: latestFieldAnswerValue,
        isAnswerValid: inputValidationResult.isInputValid,
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(
        eventTarget.id.replace('textboxID_', ''),
        JSON.stringify(fieldAnswer)
      );

      const activeFieldId = eventTarget.id.replace('textboxID_', '');

      this.msfps.helper$.next({
        activeQuestionId: activeFieldId,
        val: 'clicked on preview question input element',
      });
    });
  }


  fileName = ''
  fileExtension = ''
  selectedFile: File | null = null;
  handleFileInput(eventTarget: HTMLInputElement) {
    // SM-1679: Guard against duplicate event listeners
    if (this.initializedElements.has(eventTarget)) return;
    this.initializedElements.add(eventTarget);

    const mode = localStorage.getItem('mode') || null
    eventTarget.addEventListener('change', (fileInputEvent) => {
      // SM-1679: Handle cancelled file picker
      const file = eventTarget.files?.[0];
      if (!file) return;

      // SM-3232: Enforce the 5 MB / PDF-Word-Excel rule shown in the question note
      // before any FileReader work or network call. Reject invalid files up front
      // so the question is never marked as a Valid Response.
      const validation = validateAttachment(file);
      if (validation.valid === false) {
        eventTarget.value = '';
        this.swalWithBootstrapButtons.fire({
          title: validation.title,
          text: validation.message,
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }

      this.fileName = file.name;
      this.fileExtension = file.name.split('.').pop() || '';
      const reader = new FileReader();
      reader.onload = (e) => {

          const blob = new Blob([e.target.result]);
  
          // Log the blob as binary data
          const reader2 = new FileReader();
          reader2.onloadend = () => {
            eventTarget.removeAttribute('value');
  
            eventTarget.setAttribute('value', reader2.result ? 'FILE_UPLOADED' : '');
            const modifiedFieldEle = document.createElement('div');
            modifiedFieldEle.setAttribute(
              'style',
              'display:flex;justify-content:left',
            );
            modifiedFieldEle.innerHTML = eventTarget.outerHTML;
  
            const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
              localStorage.getItem(eventTarget.id.replace('fileID_', '').toString()),
            );
            this.selectedFile = eventTarget.files[0];
            if (this.selectedFile && mode == 'supplierEdit') {
              const formData_Scan = new FormData();
              formData_Scan.append('attachmentFile', this.selectedFile, this.selectedFile.name);
              this.buyerPreviewService.ProcessFileScan(formData_Scan).subscribe({
                next: (resp) => {
  
                  if(resp.IsSuccess){
                    const formData = new FormData();
                    formData.append('File', this.selectedFile, this.selectedFile.name)
                    formData.append('questionUniqueId', eventTarget.id.replace('fileID_', '').toString());
                    formData.append('id', this.orgFormId);
                    formData.append('FormLayout', this.formLayout);
                    this.buyerPreviewService.SaveUserSurveyAttachmentResponse(formData).subscribe({
                      next: (resp) => {
                        // SM-3232: The server is the authority on the size/type rule.
                        // If it rejects the file, surface the error and do NOT mark
                        // the question as a Valid Response.
                        if (resp?.IsSuccess !== true) {
                          eventTarget.value = '';
                          eventTarget.removeAttribute('value');
                          this.fileName = '';
                          this.fileExtension = '';
                          this.selectedFile = null;
                          this.swalWithBootstrapButtons.fire({
                            title: 'Failure!',
                            text: resp?.Message || 'Failed to upload attachment. Please try again.',
                            icon: 'error',
                            confirmButtonText: 'OK',
                          });
                          return;
                        }

                        const latestFieldAnswerValue = this.selectedFile.name
                          ? [this.selectedFile.name]
                          : null;

                        const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
                          modifiedField: this.sanitizer.bypassSecurityTrustHtml(
                            modifiedFieldEle.outerHTML,
                          ),
                          answerUniqueId: [eventTarget.id],
                          answerValue: latestFieldAnswerValue,
                          isAnswerValid: Boolean(eventTarget.value),
                          isAnswerModified: !(
                            oldFieldAnswer?.answerValue?.toString() ===
                            latestFieldAnswerValue?.toString()
                          ),
                        };       
                        localStorage.setItem(eventTarget.id.replace('fileID_', '').toString(), JSON.stringify(fieldAnswer));
        
                        const activeFieldId = eventTarget.id.replace('fileID_', '');
        
                        this.msfps.helper$.next(
                          {
                            activeQuestionId: activeFieldId,
                            val: 'clicked on preview question input element',
                          }
                        );
                      },
                      error: () => {
                        this.swalWithBootstrapButtons
                          .fire({
                            title: 'Failure!',
                            text: 'Failed to upload attachment. Please try again.',
                            icon: 'error',
                            confirmButtonText: 'OK',
                          })
                          .then(() => {});
                      },
                    });
                  }
                  else{
                    this.swalWithBootstrapButtons
                  .fire({
                    title:'Failure!',
                    text:
                      !resp.IsSuccess && resp.Message == ''
                        ? 'Some error occured'
                        : resp.Message,
                    icon: 'error',
                    confirmButtonText: 'OK',
                  })
                  .then(() => {});
                  }
                }
              })
  
   
            }else{
              
              const latestFieldAnswerValue = this.selectedFile.name
              ? [this.selectedFile.name]
              : null;
  
            const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
              modifiedField: this.sanitizer.bypassSecurityTrustHtml(
                modifiedFieldEle.outerHTML,
              ),
              answerUniqueId: [eventTarget.id],
              answerValue: latestFieldAnswerValue,
              isAnswerValid: Boolean(eventTarget.value),
              isAnswerModified: !(
                oldFieldAnswer?.answerValue?.toString() ===
                latestFieldAnswerValue?.toString()
              ),
            };
  
            localStorage.setItem(eventTarget.id.replace('fileID_', '').toString(), JSON.stringify(fieldAnswer));
  
            const activeFieldId = eventTarget.id.replace('fileID_', '');
  
            this.msfps.helper$.next(
              {
                activeQuestionId: activeFieldId,
                val: 'clicked on preview question input element',
              }
            );
            }
          };
          reader2.readAsBinaryString(blob);
        };
      reader.readAsArrayBuffer(file);
    });
  }

  handleTextAreaInput(e: Event) {
    const textAreaTarget = e.target as HTMLTextAreaElement;
    // SM-1679: Guard against duplicate event listeners
    if (this.initializedElements.has(textAreaTarget)) return;
    this.initializedElements.add(textAreaTarget);

    let timer;
    textAreaTarget.addEventListener('keyup', () => {
      // Clear the timer if the user types
      clearTimeout(timer);
      // Set up a new timer to fire after 1K milliseconds
      timer = setTimeout(() => {
        // The user has stopped typing
        this.onTextAreaInputComplete(textAreaTarget);
      }, 1170);
    });
  }

  onTextAreaInputComplete(eventTarget: HTMLTextAreaElement) {

    eventTarget.removeAttribute('value');

    if (eventTarget.value) eventTarget.setAttribute('value', eventTarget.value);
    const modifiedFieldEle = document.createElement('div');
    modifiedFieldEle.setAttribute('style', 'display:flex;justify-content:left');
    modifiedFieldEle.innerHTML = eventTarget.outerHTML;

    const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(eventTarget.id.replace('textareaID_', '').toString())
    );

    const latestFieldAnswerValue = eventTarget.value
      ? [eventTarget.value]
      : null;

    const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
      modifiedField: this.sanitizer.bypassSecurityTrustHtml(
        modifiedFieldEle.outerHTML
      ),
      answerUniqueId: [eventTarget.id],
      answerValue: latestFieldAnswerValue,
      isAnswerValid: Boolean(eventTarget.value),
      isAnswerModified: !(
        oldFieldAnswer?.answerValue?.toString() ===
        latestFieldAnswerValue?.toString()
      ),
    };

    localStorage.setItem(
      eventTarget.id.replace('textareaID_', ''),
      JSON.stringify(fieldAnswer)
    );

    const activeFieldId = eventTarget.id.replace('textareaID_', '');
    this.msfps.helper$.next({
      activeQuestionId: activeFieldId,
      val: 'clicked on preview question input element',
    });
  }
}
